import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs'
import {
  Facebook, Instagram, Linkedin, Twitter,
  Plus, CheckCircle, XCircle, Clock, Send,
  Share2, AlertCircle, Loader2, Eye,
} from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonList } from './ui/PortalSkeleton'

const PLATFORM_ICONS: Record<string, React.ComponentType<any>> = {
  instagram: Instagram,
  twitter:   Twitter,
  linkedin:  Linkedin,
  facebook:  Facebook,
}

const PLATFORM_COLORS: Record<string, string> = {
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  twitter:   'bg-sky-400',
  linkedin:  'bg-blue-600',
  facebook:  'bg-blue-500',
}

const STATUS_CONFIG: Record<string, { label: string; className: string; Icon: React.ComponentType<any> }> = {
  draft:     { label: 'Draft',           className: 'bg-zinc-100 text-zinc-600',   Icon: Clock },
  pending:   { label: 'Awaiting Approval', className: 'bg-[hsl(var(--warning)/0.18)] text-warning', Icon: Clock },
  approved:  { label: 'Approved',        className: 'bg-[hsl(var(--success)/0.15)] text-success', Icon: CheckCircle },
  rejected:  { label: 'Rejected',        className: 'bg-[hsl(var(--destructive)/0.12)] text-destructive',     Icon: XCircle },
  published: { label: 'Published',       className: 'bg-[hsl(var(--primary)/0.12)] text-primary',   Icon: Send },
}

function useOrgMembership() {
  return useQuery({
    queryKey: ['portal-social-hub-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('organization_members' as any)
        .select('org_id, role_id, status, roles(name, display_name)')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle() as any)
      return data as {
        org_id: string
        role_id: string
        status: string
        roles: { name: string; display_name: string | null }
      } | null
    },
  })
}

export function PortalSocialHubView() {
  const { toast }      = useToast()
  const queryClient    = useQueryClient()
  const [draftContent, setDraftContent] = useState('')
  const [draftPlatforms, setDraftPlatforms] = useState<string[]>([])
  const [showDraftForm, setShowDraftForm] = useState(false)

  const { data: membership } = useOrgMembership()
  const orgId    = membership?.org_id ?? null
  const roleName = membership?.roles?.name ?? ''
  const isCCO    = roleName === 'CCO' || roleName === 'President' || roleName === 'Owner'
  const isAdmin  = isCCO || roleName === 'Admin' || roleName === 'Secretary'
  const roleLabel = membership?.roles?.display_name ?? roleName

  // Connected social accounts
  const { data: accounts = [] } = useQuery({
    queryKey: ['portal-social-accounts', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('social_accounts' as any)
        .select('id, platform, account_name, account_handle, is_connected, follower_count, last_sync_at')
        .eq('org_id', orgId!)
        .eq('is_connected', true) as any)
      return (data ?? []) as any[]
    },
  })

  // Posts queue
  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ['portal-social-posts', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      const { data } = await (supabase
        .from('posts' as any)
        .select('id, content, platform, status, created_at, user_id, approved_by, rejection_reason')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(50) as any)

      const rows = (data ?? []) as any[]
      if (!rows.length) return rows

      // Fetch author names
      const userIds = [...new Set(rows.map((r: any) => r.user_id).filter(Boolean))]
      let nameMap: Record<string, string> = {}
      if (userIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', userIds as string[])
        ;(profiles ?? []).forEach((p: any) => { nameMap[p.user_id] = p.full_name || '' })
      }

      return rows.map((r: any) => ({
        ...r,
        author_name: nameMap[r.user_id] ?? '',
        is_mine: r.user_id === user?.id,
      }))
    },
  })

  // Submit post for approval
  const submitPost = useMutation({
    mutationFn: async () => {
      if (!orgId || !draftContent.trim()) throw new Error('Content required')
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase.from('posts' as any).insert(
        draftPlatforms.map(platform => ({
          org_id:   orgId,
          user_id:  user?.id,
          content:  draftContent.trim(),
          platform,
          status:   isCCO ? 'approved' : 'pending',
        }))
      ) as any)
    },
    onSuccess: () => {
      toast({ title: isCCO ? 'Post approved and queued' : 'Post submitted for approval' })
      setDraftContent('')
      setDraftPlatforms([])
      setShowDraftForm(false)
      queryClient.invalidateQueries({ queryKey: ['portal-social-posts', orgId] })
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  })

  const updatePostStatus = useMutation({
    mutationFn: async ({ postId, status, reason }: { postId: string; status: string; reason?: string }) => {
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase.from('posts' as any)
        .update({ status, approved_by: user?.id, rejection_reason: reason ?? null })
        .eq('id', postId) as any)
    },
    onSuccess: (_, vars) => {
      toast({ title: vars.status === 'approved' ? 'Post approved' : 'Post rejected' })
      queryClient.invalidateQueries({ queryKey: ['portal-social-posts', orgId] })
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  })

  const togglePlatform = (p: string) =>
    setDraftPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])

  const pendingCount = posts.filter((p: any) => p.status === 'pending').length

  if (!membership) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <Share2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p className="text-justify text-sm">You must be a member of an institution to access the Social Hub.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <PortalPageHeader
        title="Social Hub"
        description={
          isCCO
            ? `Manage your organisation's social media presence with team collaboration and approval workflows. You have final approval as ${roleLabel}.`
            : "Manage your organisation's social media presence with team collaboration and approval workflows."
        }
        action={
          <Button
            onClick={() => setShowDraftForm(v => !v)}
            className="bg-success hover:bg-success/90 text-success-foreground shrink-0 h-11"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Post
          </Button>
        }
      />

      {/* New post form */}
      {showDraftForm && (
        <div className="border border-success/30 bg-success/12/60 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Send size={14} className="text-success" />
            <span className="text-[13px] font-semibold text-success">Compose Post</span>
            {!isCCO && (
              <span className="text-[11px] text-warning bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.3)] px-2 py-0.5 rounded-full">
                Will be sent for {roleLabel === 'Admin' ? 'CCO' : 'Admin'} approval
              </span>
            )}
          </div>

          <textarea
            value={draftContent}
            onChange={e => setDraftContent(e.target.value)}
            placeholder="Write your post content…"
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg text-[13px] focus:outline-none focus:border-success bg-background resize-none"
          />

          <div>
            <p className="text-justify text-[11px] text-muted-foreground mb-2">Select platforms:</p>
            <div className="flex flex-wrap gap-2">
              {accounts.length === 0 ? (
                <p className="text-justify text-[11.5px] text-warning flex items-center gap-1">
                  <AlertCircle size={12} />
                  No connected accounts. Ask your {roleLabel ?? 'CCO'} to connect social accounts first.
                </p>
              ) : accounts.map((acc: any) => {
                const PIcon = PLATFORM_ICONS[acc.platform] ?? Share2
                const selected = draftPlatforms.includes(acc.platform)
                return (
                  <button
                    key={acc.id}
                    type="button"
                    onClick={() => togglePlatform(acc.platform)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${
                      selected
                        ? 'border-success bg-success text-success-foreground'
                        : 'border-border text-muted-foreground hover:border-success/40'
                    }`}
                  >
                    <PIcon size={13} />
                    {acc.account_handle ?? acc.platform}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setShowDraftForm(false); setDraftContent(''); setDraftPlatforms([]) }}
              className="flex-1 py-2 border border-border rounded-lg text-[12.5px] text-muted-foreground hover:bg-background"
            >
              Cancel
            </button>
            <button
              onClick={() => submitPost.mutate()}
              disabled={submitPost.isPending || !draftContent.trim() || draftPlatforms.length === 0}
              className="flex-1 py-2 bg-success text-success-foreground rounded-lg text-[12.5px] font-medium hover:bg-success disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitPost.isPending && <Loader2 size={13} className="animate-spin" />}
              {isCCO ? 'Approve & Queue' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      )}

      {/* Connected accounts */}
      {accounts.length > 0 && (
        <div>
          <p className="text-justify text-[11.5px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Connected Accounts</p>
          <div className="flex flex-wrap gap-2">
            {accounts.map((acc: any) => {
              const PIcon = PLATFORM_ICONS[acc.platform] ?? Share2
              return (
                <div key={acc.id} className="flex items-center gap-2 px-3 py-2 border border-border rounded-xl bg-background">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${PLATFORM_COLORS[acc.platform] ?? 'bg-zinc-400'}`}>
                    <PIcon size={12} />
                  </div>
                  <div>
                    <div className="text-[12.5px] font-medium text-foreground">{acc.account_handle ?? acc.account_name}</div>
                    {acc.follower_count > 0 && (
                      <div className="text-[10.5px] text-muted-foreground">{acc.follower_count.toLocaleString()} followers</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Post queue tabs */}
      <Tabs defaultValue={isCCO && pendingCount > 0 ? 'pending' : 'all'}>
        <TabsList>
          <TabsTrigger value="all">All Posts</TabsTrigger>
          <TabsTrigger value="pending" className="relative">
            Pending Approval
            {pendingCount > 0 && (
              <span className="ml-1.5 bg-warning text-warning-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
        </TabsList>

        {(['all','pending','approved','published'] as const).map(tab => (
          <TabsContent key={tab} value={tab} className="mt-4">
            {postsLoading ? (
              <PortalSkeletonList rows={3} />
            ) : (() => {
              const filtered = tab === 'all'
                ? posts
                : posts.filter((p: any) => p.status === tab)

              if (!filtered.length) return (
                <PortalEmptyState
                  icon={Clock}
                  message={tab === 'pending' ? 'No posts waiting for approval.' : 'No posts here yet.'}
                />
              )

              return (
                <div className="space-y-3">
                  {filtered.map((post: any) => {
                    const cfg  = STATUS_CONFIG[post.status] ?? STATUS_CONFIG.draft
                    const PIcon = PLATFORM_ICONS[post.platform] ?? Share2
                    return (
                      <div key={post.id} className="border border-border rounded-xl p-4 space-y-2 bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0 ${PLATFORM_COLORS[post.platform] ?? 'bg-zinc-400'}`}>
                              <PIcon size={11} />
                            </div>
                            <span className="text-[11.5px] font-medium text-muted-foreground capitalize">{post.platform}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1 ${cfg.className}`}>
                              <cfg.Icon size={10} />
                              {cfg.label}
                            </span>
                          </div>
                          <span className="text-[11px] text-muted-foreground shrink-0">
                            {new Date(post.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                        </div>

                        <p className="text-justify text-[13px] text-foreground leading-relaxed">{post.content}</p>

                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Eye size={11} />
                            <span>by {post.author_name}</span>
                          </div>

                          {/* CCO / Admin approval actions */}
                          {isCCO && post.status === 'pending' && (
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => updatePostStatus.mutate({ postId: post.id, status: 'rejected' })}
                                className="flex items-center gap-1 px-2.5 py-1 border border-[hsl(var(--destructive)/0.3)] text-destructive rounded-lg text-[11px] font-medium hover:bg-[hsl(var(--destructive)/0.08)]"
                              >
                                <XCircle size={11} />
                                Reject
                              </button>
                              <button
                                onClick={() => updatePostStatus.mutate({ postId: post.id, status: 'approved' })}
                                className="flex items-center gap-1 px-2.5 py-1 bg-success text-success-foreground rounded-lg text-[11px] font-medium hover:bg-success"
                              >
                                <CheckCircle size={11} />
                                Approve
                              </button>
                            </div>
                          )}

                          {/* Show rejection reason */}
                          {post.status === 'rejected' && post.rejection_reason && (
                            <span className="text-[11px] text-destructive italic">"{post.rejection_reason}"</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
